import React, { useEffect, useRef, useState } from "react";
import styles from "./InventoryContextMenu.module.scss";
import { Button, ButtonVariant } from "@src/components/button/Button";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { eventActions, pricingActions, purchasingActions, settingsActions, systemActions } from "@src/store";
import {
	HighlightedSectionEnum,
	ManageInventoryTabEnum,
	SelectedManageInventoryTabEnum,
} from "@src/store/pricing/selectionSlice";
import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import { externalAutoPricerRowClick } from "@src/pages/home/children/pricing/components/inventory/Inventory";
import { requestAutopricerExit } from "@src/pages/home/children/pricing/components/manage-inventory/autopricerExitGuard";
import { MixpanelCategory, mixpanelTrack } from "@src/components/utils/mixPanelUtils";
import { toast } from "sonner";
import { NewTag } from "@src/components/newTag/NewTag";

type TInventoryContextMenu = {
	selectedRow: EventInventoryRecord | null;
	closeContextMenu: () => void;
	onHelpOpen: () => void;
	onContactUserOpen: () => void;
	onSchedulePriceOpen: () => void;
	onFlashSaleOpen: () => void;
	onPurchaseInfoOpen: () => void;
	onTagOpen?: () => void;
	onSplitListingsOpen: () => void;
	onExternalNotesModalOpen: () => void;
	onShareUnshareModalOpen: () => void;
	onInternalNotesModalOpen: () => void;
	onRelistOpen: () => void;
	onEDeliveryOpen: () => void;
	onDuplicateOpen: () => void;
	onViewHistoryOpen: () => void;
	onCreateInvoiceOpen?: () => void;
	onInternalInvoiceOpen?: () => void;
	onChangeSeatsOpen?: () => void;
};

const InventoryContextMenu: React.FC<TInventoryContextMenu> = ({
	selectedRow,
	closeContextMenu,
	onHelpOpen,
	onContactUserOpen,
	onSchedulePriceOpen,
	onFlashSaleOpen,
	onPurchaseInfoOpen,
	onTagOpen,
	onSplitListingsOpen,
	onExternalNotesModalOpen,
	onShareUnshareModalOpen,
	onInternalNotesModalOpen,
	onRelistOpen,
	onEDeliveryOpen,
	onDuplicateOpen,
	onViewHistoryOpen,
	onCreateInvoiceOpen,
	onInternalInvoiceOpen,
	onChangeSeatsOpen,
}) => {
	const dispatch = useAppDispatch();
	const locations = useAppSelector((state) => state.system.locations.data);
	const locationsInit = useAppSelector((state) => state.system.locations.init);
	const locationsLoading = useAppSelector((state) => state.system.locations.loading);

	useEffect(() => {
		if (locationsInit || locationsLoading) return;
		dispatch(systemActions.listLocations({ _dc: String(Date.now()) }));
	}, [dispatch, locationsInit, locationsLoading]);

	const deliveryMethods = locations.filter((l) => l.Location_ID != null && l.Location_Name && l.Location_Name.trim().length > 0);

	const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
	const [loadingMethodId, setLoadingMethodId] = useState<number | "not-in-hand" | null>(null);
	const submenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const cancelSubmenuClose = () => {
		if (submenuCloseTimerRef.current) {
			clearTimeout(submenuCloseTimerRef.current);
			submenuCloseTimerRef.current = null;
		}
	};

	const scheduleSubmenuClose = () => {
		cancelSubmenuClose();
		submenuCloseTimerRef.current = setTimeout(() => {
			setIsSubmenuOpen(false);
		}, 150);
	};

	const openSubmenu = () => {
		cancelSubmenuClose();
		setIsSubmenuOpen(true);
	};

	const selectedId = selectedRow?.Item_ID;
	const isSold = selectedRow?.Invoice_ID;
	const isInHand = !!selectedRow?.InHand;
	const isEDelivery = !!selectedRow?.SH_eDelivery;
	const numOnHold = (selectedRow as { Num_OnHold?: number } | null)?.Num_OnHold ?? 0;
	const isOnHold = !!selectedRow?.On_Hold || numOnHold > 0;
	const showMarkInHand = !isSold && !isEDelivery;
	const showReleaseListing = !isSold && isOnHold;

	const hasEditSeats = useAppSelector((state) => state.settings.hasEditSeats);
	const hasForceEditSeats = useAppSelector((state) => state.settings.hasForceEditSeats);
	const hasStageFrontRole = useAppSelector((state) => state.settings.hasStageFrontRole);
	const isAdmin = useAppSelector((state) => state.settings.isAdmin);

	const showContactUser = hasStageFrontRole;

	const anyPdfAttached = (selectedRow?.totalPDFsAttached ?? 0) > 0 || !!selectedRow?.PDF_Attached;
	const showChangeSeats =
		!!onChangeSeatsOpen && ((hasEditSeats && anyPdfAttached) || hasForceEditSeats || hasStageFrontRole || isAdmin);

	const releaseListingLoading = useAppSelector((state) => state.pricing.releaseListings.loading);

	const reviewedListingIds = useAppSelector((state) => state.pricing.reviewedListings.listingIds);
	const isReviewed = !!selectedRow?.Listing_ID && reviewedListingIds.includes(selectedRow.Listing_ID);
	const [isReviewing, setIsReviewing] = useState(false);

	const handleToggleReviewed = async () => {
		const listingId = selectedRow?.Listing_ID;
		if (!listingId) return;

		if (isReviewed) {
			dispatch(pricingActions.unmarkListingsReviewed([listingId]));
			toast.success("Reviewed mark removed.");
			closeContextMenu();
			return;
		}

		setIsReviewing(true);
		try {
			await dispatch(
				pricingActions.updateListingPrices([
					{ listing_id: listingId, price: selectedRow?.Item_Price ?? 0, face: selectedRow?.Face ?? null },
				]),
			).unwrap();
			dispatch(pricingActions.markListingsReviewed([listingId]));
			toast.success("Ticket marked as reviewed.");
			closeContextMenu();
		} catch {
			toast.error("Failed to mark ticket as reviewed.");
		} finally {
			setIsReviewing(false);
		}
	};

	const handleReleaseListing = async () => {
		const listingId = selectedRow?.Listing_ID;
		if (!listingId) return;
		try {
			await dispatch(pricingActions.releaseListings({ ListingIds: [listingId] })).unwrap();
			toast.success("Listing released.");
		} catch {
			toast.error("Failed to release listing.");
		} finally {
			closeContextMenu();
		}
	};

	const handleSetInventoryId = () => {
		if (!selectedId) return;
		dispatch(
			eventActions.setInventory({
				inventoryItemIds: [selectedId],
				isEditListingEnabled: true,
			}),
		);
	};

	const handleEditButtonClick = ({ tab, section }: { tab: SelectedManageInventoryTabEnum; section: HighlightedSectionEnum }) => {
		if (!selectedId) return;
		requestAutopricerExit(() => {
			handleSetInventoryId();
			dispatch(eventActions.setManageInventoryTab(ManageInventoryTabEnum.ManageListing));
			dispatch(eventActions.setSelectedInventoryTab(SelectedManageInventoryTabEnum[tab]));
			dispatch(eventActions.setHighlightedSection(section));
		});

		closeContextMenu();
	};

	const openAutopricerPopup = () => {
		dispatch(settingsActions.setAutoPricerPopup(true));
	};

	const openSingleAutopricer = () => {
		dispatch(eventActions.setInventory({ isEditListingEnabled: true }));
		dispatch(eventActions.setManageInventoryTab(ManageInventoryTabEnum.Autopricer));
	};

	const handleOpenAutopricer = () => {
		if (!selectedId) return;
		requestAutopricerExit(() => {
			externalAutoPricerRowClick(selectedRow);

			dispatch(
				eventActions.setInventory({
					inventoryItemIds: [selectedId],
				}),
			);

			if (selectedRow?.Is_AutoPriced) {
				openAutopricerPopup();
			} else {
				openSingleAutopricer();
			}
		});

		closeContextMenu();
	};

	const handleHelpAndSupportOpen = () => {
		onHelpOpen();
		closeContextMenu();
	};

	const handleContactUserOpen = () => {
		onContactUserOpen();
		closeContextMenu();
	};

	const handleCreateInvoiceOpen = () => {
		onCreateInvoiceOpen?.();
		closeContextMenu();
	};

	const handleInternalInvoiceOpen = () => {
		onInternalInvoiceOpen?.();
		closeContextMenu();
	};

	const handleMixpaneTrack = (e: React.MouseEvent<HTMLButtonElement>) => {
		const trackingElement = e.currentTarget.name as MixpanelCategory;
		if (!trackingElement) return;

		mixpanelTrack(trackingElement);
	};

	const handleMarkInHand = async (markAsInHand: boolean, locationId?: number) => {
		const listingId = selectedRow?.Listing_ID;
		if (!listingId) return;

		setLoadingMethodId(markAsInHand ? (locationId ?? null) : "not-in-hand");

		try {
			await dispatch(
				purchasingActions.updateListingInHand({
					ListingIds: [listingId],
					InHand: markAsInHand,
					...(markAsInHand && locationId != null ? { LocationId: locationId } : {}),
				}),
			).unwrap();

			toast.success(markAsInHand ? "Marked tickets as In-Hand" : "Marked tickets as Not In-Hand");
			closeContextMenu();
		} catch {
			toast.error("Failed to update In-Hand status");
		} finally {
			setLoadingMethodId(null);
		}
	};

	const eDeliveryLabel = selectedRow?.SH_eDelivery ? "Remove E-Delivery" : "Add E-Delivery";
	const eDeliveryMixpanel = selectedRow?.SH_eDelivery ? MixpanelCategory.REMOVE_E_DELIVERY : MixpanelCategory.ADD_E_DELIVERY;

	return (
		<div className={styles.inventoryContextRoot}>
			<Button
				onClick={(e) => {
					onSchedulePriceOpen();
					handleMixpaneTrack(e);
					closeContextMenu();
				}}
				name={MixpanelCategory.SCHEDULE_PRICER}
				variant={ButtonVariant.Borderless}
				controlSize="xsm">
				Schedule Price <NewTag tagId="inventorySchedulePrice" />
			</Button>
			<Button
				onClick={(e) => {
					onFlashSaleOpen();
					handleMixpaneTrack(e);
					closeContextMenu();
				}}
				name={MixpanelCategory.FLASH_SALE}
				variant={ButtonVariant.Borderless}
				controlSize="xsm">
				Flash Sale <NewTag tagId="inventoryFlashSale" />
			</Button>
			{!isSold ? (
				<Button
					onClick={(e) => {
						handleOpenAutopricer();
						handleMixpaneTrack(e);
					}}
					name={MixpanelCategory.AUTOPRICER_MENU}
					variant={ButtonVariant.Borderless}
					controlSize="xsm">
					Autopricer <NewTag tagId="inventoryAutopricer" />
				</Button>
			) : null}
			{!isSold ? (
				<Button
					onClick={handleToggleReviewed}
					isLoading={isReviewing}
					disabled={isReviewing}
					variant={ButtonVariant.Borderless}
					controlSize="xsm">
					{isReviewed ? "Unmark Reviewed" : "Mark Ticket Reviewed"} <NewTag tagId="inventoryMarkTicketReviewed" />
				</Button>
			) : null}
			{showMarkInHand && (
				<div
					className={styles.submenuRoot}
					onMouseEnter={() => !isInHand && openSubmenu()}
					onMouseLeave={() => !isInHand && scheduleSubmenuClose()}>
					<Button
						onClick={() => {
							if (isInHand) handleMarkInHand(false);
						}}
						isLoading={loadingMethodId === "not-in-hand"}
						disabled={loadingMethodId !== null}
						variant={ButtonVariant.Borderless}
						controlSize="xsm">
						<div className={styles.submenuTrigger}>
							<span>
								{isInHand ? "Mark Tickets Not In Hand" : "Mark Tickets In Hand"}{" "}
								<NewTag tagId="inventoryMarkTicketsInHand" />
							</span>
							{!isInHand && <span className={styles.chevron}>›</span>}
						</div>
					</Button>
					{!isInHand && isSubmenuOpen && (
						<div className={styles.submenuPanel} onMouseEnter={openSubmenu} onMouseLeave={scheduleSubmenuClose}>
							{deliveryMethods.map((loc) => (
								<Button
									key={loc.Location_ID ?? loc.Location_Name}
									onClick={() => {
										if (loc.Location_ID != null) handleMarkInHand(true, loc.Location_ID);
									}}
									isLoading={loadingMethodId === loc.Location_ID}
									disabled={loadingMethodId !== null}
									variant={ButtonVariant.Borderless}
									controlSize="xsm">
									{loc.Location_Name.trim()}
								</Button>
							))}
						</div>
					)}
				</div>
			)}

			{onInternalInvoiceOpen && !isSold ? (
				<Button onClick={handleInternalInvoiceOpen} disabled={isOnHold} variant={ButtonVariant.Borderless} controlSize="xsm">
					Internal Invoice <NewTag tagId="inventoryInternalInvoice" />
				</Button>
			) : null}
			{onCreateInvoiceOpen && !isSold ? (
				<Button onClick={handleCreateInvoiceOpen} disabled={isOnHold} variant={ButtonVariant.Borderless} controlSize="xsm">
					Create Invoice <NewTag tagId="inventoryCreateInvoice" />
				</Button>
			) : null}
			<Button
				onClick={(e) => {
					onShareUnshareModalOpen();
					handleMixpaneTrack(e);
					closeContextMenu();
				}}
				disabled={isOnHold}
				name={MixpanelCategory.SHARE_UNSHARE}
				variant={ButtonVariant.Borderless}
				controlSize="xsm">
				Share/Unshare <NewTag tagId="inventoryShareUnshare" />
			</Button>
			<Button
				onClick={(e) => {
					onSplitListingsOpen();
					handleMixpaneTrack(e);
					closeContextMenu();
				}}
				disabled={isOnHold}
				name={MixpanelCategory.SPLIT_LISTING}
				variant={ButtonVariant.Borderless}
				controlSize="xsm">
				Split Listing <NewTag tagId="inventorySplitListing" />
			</Button>
			<Button
				onClick={(e) => {
					onDuplicateOpen();
					handleMixpaneTrack(e);
					closeContextMenu();
				}}
				name={MixpanelCategory.DUPLICATE_LISTING}
				variant={ButtonVariant.Borderless}
				controlSize="xsm">
				Duplicate Listing <NewTag tagId="inventoryDuplicateListing" />
			</Button>
			<Button
				onClick={(e) => {
					onEDeliveryOpen();
					handleMixpaneTrack(e);
					closeContextMenu();
				}}
				name={eDeliveryMixpanel}
				variant={ButtonVariant.Borderless}
				controlSize="xsm">
				{eDeliveryLabel} <NewTag tagId="inventoryEDelivery" />
			</Button>
			<Button
				onClick={(e) => {
					onRelistOpen();
					handleMixpaneTrack(e);
					closeContextMenu();
				}}
				disabled={isOnHold}
				name={MixpanelCategory.RELIST}
				variant={ButtonVariant.Borderless}
				controlSize="xsm">
				Re-list Listing <NewTag tagId="inventoryRelistListing" />
			</Button>
			<Button
				onClick={(e) => {
					onInternalNotesModalOpen();
					handleMixpaneTrack(e);
					closeContextMenu();
				}}
				name={MixpanelCategory.INTERNAL_NOTES}
				variant={ButtonVariant.Borderless}
				controlSize="xsm">
				Internal Notes <NewTag tagId="inventoryInternalNotes" />
			</Button>
			<Button
				onClick={(e) => {
					onExternalNotesModalOpen();
					handleMixpaneTrack(e);
					closeContextMenu();
				}}
				name={MixpanelCategory.EXTERNAL_NOTES}
				variant={ButtonVariant.Borderless}
				controlSize="xsm">
				External Notes <NewTag tagId="inventoryExternalNotes" />
			</Button>
			<Button
				onClick={(e) => {
					handleEditButtonClick({ tab: SelectedManageInventoryTabEnum.Basic, section: HighlightedSectionEnum.ListingInfo });
					handleMixpaneTrack(e);
				}}
				name={MixpanelCategory.LISTING_INFO}
				variant={ButtonVariant.Borderless}
				controlSize="xsm">
				Listing Info <NewTag tagId="inventoryListingInfo" />
			</Button>
			{onTagOpen ? (
				<Button
					onClick={(e) => {
						onTagOpen();
						handleMixpaneTrack(e);
						closeContextMenu();
					}}
					name={MixpanelCategory.ADD_TAG}
					variant={ButtonVariant.Borderless}
					controlSize="xsm">
					Add Tag <NewTag tagId="inventoryAddTag" />
				</Button>
			) : null}
			<Button
				onClick={(e) => {
					onPurchaseInfoOpen();
					handleMixpaneTrack(e);
					closeContextMenu();
				}}
				name={MixpanelCategory.PURCHASE_INFORMATION}
				variant={ButtonVariant.Borderless}
				controlSize="xsm">
				Purchase Information <NewTag tagId="inventoryPurchaseInformation" />
			</Button>
			<Button
				onClick={(e) => {
					handleEditButtonClick({ tab: SelectedManageInventoryTabEnum.Basic, section: HighlightedSectionEnum.Tickets });
					handleMixpaneTrack(e);
				}}
				name={MixpanelCategory.TICKETS}
				variant={ButtonVariant.Borderless}
				controlSize="xsm">
				Tickets <NewTag tagId="inventoryTickets" />
			</Button>
			{showChangeSeats ? (
				<Button
					onClick={() => {
						onChangeSeatsOpen?.();
						closeContextMenu();
					}}
					variant={ButtonVariant.Borderless}
					controlSize="xsm">
					Change Seat Numbers
				</Button>
			) : null}
			<Button
				onClick={(e) => {
					handleEditButtonClick({ tab: SelectedManageInventoryTabEnum.Basic, section: HighlightedSectionEnum.AttachPDF });
					handleMixpaneTrack(e);
				}}
				name={MixpanelCategory.ATTACH_PDFS}
				variant={ButtonVariant.Borderless}
				controlSize="xsm">
				Attach PDF's <NewTag tagId="inventoryAttachPdfs" />
			</Button>
			{showReleaseListing ? (
				<Button
					onClick={(e) => {
						handleReleaseListing();
						handleMixpaneTrack(e);
					}}
					isLoading={releaseListingLoading}
					disabled={releaseListingLoading}
					variant={ButtonVariant.Borderless}
					controlSize="xsm">
					Release Listing <NewTag tagId="inventoryReleaseListing" />
				</Button>
			) : null}
			<Button
				onClick={(e) => {
					onViewHistoryOpen();
					handleMixpaneTrack(e);
					closeContextMenu();
				}}
				name={MixpanelCategory.VIEW_HISTORY}
				variant={ButtonVariant.Borderless}
				controlSize="xsm">
				View History <NewTag tagId="inventoryViewHistory" />
			</Button>
			{showContactUser && (
				<Button onClick={handleContactUserOpen} variant={ButtonVariant.Borderless} controlSize="xsm">
					Contact User <NewTag tagId="inventoryContactUser" />
				</Button>
			)}
			<Button
				onClick={(e) => {
					handleHelpAndSupportOpen();
					handleMixpaneTrack(e);
				}}
				name={MixpanelCategory.HELP_SUPPORT}
				variant={ButtonVariant.Borderless}
				controlSize="xsm">
				Help & Support <NewTag tagId="inventoryHelpAndSupport" />
			</Button>
		</div>
	);
};

export default InventoryContextMenu;
